import { Box, Icon, Text, TextButton } from '@zillow/constellation-10';
import { IconMailFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserListingConversationWebApiHandler_getConversationCountsForListing } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import ConnectedModuleCard from '../../../../../srcLegacy/shared/components/ConnectedModuleCard';
import EmptyState from '../../../../../srcLegacy/shared/components/EmptyState';
import SimpleCard from '../../../../../srcLegacy/shared/components/SimpleCard';

type Props = {
  listingAlias: string;
};

const CARD_LABELS = {
  NEW: 'New',
  UNREPLIED: 'Unreplied',
  TOTAL: 'Total',
};

const InquiriesModule = ({ listingAlias }: Props) => {
  const { data, isPending, isError } =
    useRentalManagerUserListingConversationWebApiHandler_getConversationCountsForListing(
      {
        listingAlias,
        calculateInRealTime: true,
      },
      { enabled: Boolean(listingAlias), staleTime: 8 * 1000 },
    );

  const inquiriesPath = `/rental-manager/lead-management/${listingAlias}`;

  const { unreadCount = 0, notRepliedToCount = 0, totalConversationsCount = 0 } = data || {};
  const hasConversations = totalConversationsCount > 0;

  const renderCTA = () => {
    if (!hasConversations && !isPending) return null;

    return (
      <TextButton tone="brand" asChild>
        <a href={inquiriesPath} target="_self">
          View in messages
        </a>
      </TextButton>
    );
  };

  const handleCardClick = (filter: string) => {
    const queryParams = filter ? `?${filter}=true&byProperty=true` : '?byProperty=true';
    window.location.href = `${inquiriesPath}${queryParams}`;
  };

  const renderContent = () => {
    if (isError) {
      return (
        <EmptyState
          icon={<Icon render={<IconMailFilled />} size="md" css={{ color: 'icon.subtle' }} />}
          heading="Unable to load inquiries"
          headingLevel={4}
          body="Please refresh the page to try again."
          align="left"
        />
      );
    }

    if (!hasConversations && !isPending) {
      return (
        <EmptyState
          data-testid="EmptyInquiriesCardSimple"
          icon={<Icon render={<IconMailFilled />} size="md" css={{ color: 'icon.subtle' }} />}
          heading="No inquiries yet"
          headingLevel={4}
          align="left"
          body="When a renter contacts you, help make a good first impression by responding within 24 hours."
        />
      );
    }

    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(1, minmax(0,1fr))',
          gap: { base: '200', lg: '400' },
        }}
      >
        <Box data-testid="Grid-New">
          <SimpleCard
            cursor="pointer"
            withChevron={true}
            onClick={() => handleCardClick('unreadOnly')}
            isLoading={isPending}
          >
            <Box
              css={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text textStyle="body-sm">{CARD_LABELS.NEW}</Text>
              {typeof unreadCount === 'number' && (
                <Text textStyle="body-sm-bold" css={{ marginTop: isPending ? '0' : '200' }}>
                  {unreadCount}
                </Text>
              )}
            </Box>
          </SimpleCard>
        </Box>

        <Box data-testid="Grid-Unreplied">
          <SimpleCard
            cursor="pointer"
            withChevron={true}
            onClick={() => handleCardClick('notRepliedToOnly')}
            isLoading={isPending}
          >
            <Box
              css={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text textStyle="body-sm">{CARD_LABELS.UNREPLIED}</Text>
              {typeof notRepliedToCount === 'number' && (
                <Text textStyle="body-sm-bold" css={{ marginTop: isPending ? '0' : '200' }}>
                  {notRepliedToCount}
                </Text>
              )}
            </Box>
          </SimpleCard>
        </Box>

        <Box data-testid="Grid-Total">
          <SimpleCard cursor="pointer" withChevron={true} onClick={() => handleCardClick('')} isLoading={isPending}>
            <Box
              css={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text textStyle="body-sm">{CARD_LABELS.TOTAL}</Text>
              {typeof totalConversationsCount === 'number' && (
                <Text textStyle="body-sm-bold" css={{ marginTop: isPending ? '0' : '200' }}>
                  {totalConversationsCount}
                </Text>
              )}
            </Box>
          </SimpleCard>
        </Box>
      </Box>
    );
  };

  return (
    <div data-testid="InquiriesModule">
      <ConnectedModuleCard
        title="Inquiries"
        ctaComponent={renderCTA()}
        ctaFontType="body-sm"
        justifyHeadingContent="center"
      >
        {renderContent()}
      </ConnectedModuleCard>
    </div>
  );
};

export default InquiriesModule;
